import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/menu`;

// Fetch the menu items for a user, using localStorage cache if available
export const getMenu = async (userId, forceRefresh = false) => {
    try {
        if(!userId || userId === "undefined" || userId === "null") {
            console.error(" Invalid User ID provided:", userId);
            return { general: [], core_admin: [] };
        }

        // Ensure userId is a valid number
        const numericUserId = parseInt(userId, 10);
        if(isNaN(numericUserId) || numericUserId <= 0) {
            console.error(" Invalid User ID format:", userId);
            return { general: [], core_admin: [] };
        }

        if(!forceRefresh) {
            const cachedMenu = localStorage.getItem(`menu:${numericUserId}`);

            if(cachedMenu) {
                console.log(" Menu Loaded from LocalStorage");

                const parsedMenu = JSON.parse(cachedMenu);
                const userRole = localStorage.getItem("userRole");

                // Return entire menu if admin, else filter if it's an array
                if(
                    typeof parsedMenu === "object" &&
                    !Array.isArray(parsedMenu)
                ) {
                    return parsedMenu;
                }

                return userRole === "admin"
                    ? parsedMenu
                    : parsedMenu.filter((item) => item.is_enabled === 1);
            }
        }

        // console.log(" Fetching fresh menu from backend...");
        const response = await axios.get(`${API_URL}/${numericUserId}`, {
            withCredentials: true,
        });

        if(
            !response.data ||
            (Array.isArray(response.data) && response.data.length === 0)
        ) {
            console.warn(
                " API returned empty menu, displaying 'No Menu Available'.",
            );

            return { general: [], core_admin: [] };
        }

        localStorage.setItem(
            `menu:${numericUserId}`,
            JSON.stringify(response.data),
        );

        const userRole = localStorage.getItem("userRole");

        // If structured object (expected case), return as-is
        if(
            typeof response.data === "object" &&
            !Array.isArray(response.data)
        ) {
            return response.data;
        }

        // Fallback: if data is unexpectedly an array, convert to expected structure
        const arrayData =
            userRole === "admin"
                ? response.data
                : response.data.filter((item) => item.is_enabled === 1);

        // Convert array to expected structure
        const general = arrayData.filter((item) => item.is_core_admin === 0);
        const core_admin = arrayData.filter((item) => item.is_core_admin === 1);

        return { general, core_admin };
    } catch(error) {
        console.error(" Error fetching menu:", error);

        return { general: [], core_admin: [] };
    }
};

// Refresh the menu cache for a user and fetch the latest menu
export const refreshMenuCache = async (userId) => {
    try {
        if(!userId || userId === "undefined" || userId === "null") {
            console.error("Invalid userId in refreshMenuCache:", userId);
            return { general: [], core_admin: [] };
        }

        const numericUserId = parseInt(userId, 10);
        if(isNaN(numericUserId) || numericUserId <= 0) {
            console.error("Invalid userId format in refreshMenuCache:", userId);
            return { general: [], core_admin: [] };
        }

        // console.log(" Clearing Cache...");
        await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/menu/reset/${numericUserId}`,
            {},
            { withCredentials: true },
        );

        // Step 1: Remove from LocalStorage
        localStorage.removeItem(`menu:${numericUserId}`);
        // console.log(" LocalStorage Cache Cleared");

        // Step 2: Fetch New Menu Immediately After Cache Reset
        // console.log(" Fetching fresh menu after cache reset...");
        return await getMenu(numericUserId, true);
    } catch(error) {
        console.error(" Error clearing cache:", error);

        return { general: [], core_admin: [] };
    }
};

// Refresh specifically the menu cache (for security group changes)
export const refreshMenuCacheOnly = async (userId) => {
    try {
        if(!userId || userId === "undefined" || userId === "null") {
            console.error("Invalid userId in refreshMenuCacheOnly:", userId);
            return { success: false, error: "Invalid userId" };
        }

        const numericUserId = parseInt(userId, 10);
        if(isNaN(numericUserId) || numericUserId <= 0) {
            console.error(
                "Invalid userId format in refreshMenuCacheOnly:",
                userId,
            );
            return { success: false, error: "Invalid userId format" };
        }

        console.log("Clearing menu-specific cache...");
        await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/menu/reset-menu/${numericUserId}`,
            {},
            { withCredentials: true },
        );

        // Remove from LocalStorage
        localStorage.removeItem(`menu:${numericUserId}`);
        console.log("Menu cache cleared for user:", numericUserId);

        return { success: true };
    } catch(error) {
        console.error("Error clearing menu cache:", error);
        return { success: false, error };
    }
};
